import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { handleNotifications } from '../../../components/generic/utils/handleNotifications'
import { Auth } from '../../../components/modules/API/Auth/auth'
import { IChangePasswordData } from '../../../components/modules/API/Auth/models/recovery'

export const useForgotForm = () => {
  const navigate = useNavigate()
  const [isShowSmsStep, setIsShowSmsStep] = useState<boolean>(false)
  const [isMatchCodeRecovery, setIsMatchCodeRecovery] = useState<boolean>(false)
  const [isAwaitingCodeVerifying, setIsAwaitingCodeVerifying] = useState<boolean>(false)
  const [tel, setTel] = useState<string>('')
  const [codeSMS, setCodeSMS] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const restDidgitsTel = useMemo(() => 10 - tel.length, [tel])

  const handleTelephone = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value && !/^\d+$/.test(e.target.value)) return
    if (!e.target.value[0]) {
      setTel('')
      return
    }
    if (e.target.value[0] !== '0') return
    if (e.target.value.length > 10) return
    setTel(e.target.value)
  }

  const handlePassword = (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
  const handleConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) =>
    setConfirmPassword(e.target.value)

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isMatchCodeRecovery) return
    setUserName(e.target.value)
  }

  const handleCodeSms = (code: string) => setCodeSMS(code)

  const cancelSmsStep = () => {
    setIsShowSmsStep(false)
    setCodeSMS('')
  }

  const verifyRecoveryCode = async () => {
    setIsAwaitingCodeVerifying(true)
    try {
      await Auth.checkRecoveryCode(codeSMS, tel, userName)
      setIsShowSmsStep(false)
      setIsMatchCodeRecovery(true)
    } catch (error: any) {
      if (error?.response?.status === 405) {
        handleNotifications('wrong code', { type: 'error' })
      } else {
        handleNotifications('משהו השתבש', { type: 'error' })
      }
    } finally {
      setIsAwaitingCodeVerifying(false)
    }
  }

  const sendSmsRecovery = async () => {
    if (tel.length !== 10 || !userName) return
    try {
      await Auth.sendRecoveryCode(tel, userName)
      setIsShowSmsStep(true)
    } catch (error: any) {
      handleNotifications(error?.response?.data?.error || 'שגיאה בשליחת SMS', { type: 'error' })
    }
  }

  const isAlertPassword = useMemo(
    () => !password || password.length < 4 || !confirmPassword || password !== confirmPassword,
    [password, confirmPassword],
  )

  const handleChangePassword = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault()
    if (!isMatchCodeRecovery || isAlertPassword) return
    try {
      const putData: IChangePasswordData = {
        Code: codeSMS,
        Password: password,
        Phone: tel,
        UserID: userName,
      }
      await Auth.changePassword(putData)
      handleNotifications('הסיסמה שונתה בהצלחה', { type: 'success' })
      navigate('/login', { replace: true })
    } catch {
      handleNotifications('משהו השתבש', { type: 'error' })
    }
  }

  return {
    tel,
    userName,
    password,
    confirmPassword,
    restDidgitsTel,
    codeSMS,
    isShowSmsStep,
    isMatchCodeRecovery,
    isAlertPassword,
    isAwaitingCodeVerifying,
    handleTelephone,
    handlePassword,
    handleConfirmPassword,
    handleUserName,
    handleCodeSms,
    sendSmsRecovery,
    verifyRecoveryCode,
    cancelSmsStep,
    handleChangePassword,
  }
}

export type UseForgotFormReturn = ReturnType<typeof useForgotForm>
